import React, { useState, useEffect, useMemo } from 'react';
import MAJOR_CITIES from '../data/cities';
import { useCombobox } from '../hooks/useCombobox';

const CitySearch = ({ value, onChange, placeholder }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(value || '');

  // Keep local input in sync if parent updates `value` (e.g. via geolocation).
  useEffect(() => {
    if (value !== undefined && value !== searchTerm) setSearchTerm(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const suggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const searchLower = searchTerm.toLowerCase();
    return MAJOR_CITIES
      .filter(city =>
        city.name.toLowerCase().includes(searchLower) ||
        city.fullName.toLowerCase().includes(searchLower)
      )
      .slice(0, 10);
  }, [searchTerm]);

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion.fullName);
    setSearchTerm(suggestion.fullName);
  };

  const { open, setOpen, activeIndex, containerRef, handleKeyDown } = useCombobox(
    suggestions,
    handleSuggestionClick
  );

  const listboxId = 'city-search-listbox';
  const showLocationButton = open && !searchTerm;
  const showSuggestions = open && suggestions.length > 0;

  const getLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
      );

      if (!response.ok) {
        throw new Error('Failed to get city name');
      }

      const data = await response.json();
      if (data.city) {
        const matchedCity = MAJOR_CITIES.find(city =>
          city.name.toLowerCase() === data.city.toLowerCase()
        );
        const newValue = matchedCity ? matchedCity.fullName : data.city;
        onChange(newValue);
        setSearchTerm(newValue);
        setOpen(false);
      } else {
        setError('Could not determine your city');
      }
    } catch (err) {
      setError(err.message === 'User denied Geolocation'
        ? 'Please enable location access to use this feature'
        : 'Could not determine your location');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setOpen(true);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <input
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? `city-option-${activeIndex}` : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="input-field pl-10"
        />
      </div>

      {(showLocationButton || showSuggestions) && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-30 w-full mt-2 bg-paper border border-line rounded-2xl shadow-lift list-none p-1.5 overflow-hidden"
        >
          {showLocationButton && (
            <li role="presentation">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={getLocation}
                disabled={loading}
                className="w-full px-3.5 py-3 text-left hover:bg-vellum/60 flex items-center gap-3 text-ink-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta transition-colors"
              >
                <svg className="h-5 w-5 text-terracotta" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-ink-3" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Locating you…
                  </span>
                ) : (
                  <span className="text-sm">Use current location</span>
                )}
              </button>
            </li>
          )}

          {suggestions.map((suggestion, idx) => (
            <li
              key={suggestion.fullName}
              id={`city-option-${idx}`}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-3.5 py-2.5 cursor-pointer text-ink rounded-xl transition-colors ${
                idx === activeIndex ? 'bg-vellum' : 'hover:bg-vellum/60'
              }`}
            >
              {suggestion.fullName}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="absolute text-xs text-terracotta-deep mt-2">{error}</p>
      )}
    </div>
  );
};

export default CitySearch; 